import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { Hash, MessagesSquare, Search, Users2 } from "lucide-react";
import { api } from "@/lib/api";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { cn, colorFromString, fmtTime } from "@/lib/utils";
import { useWorkspace } from "@/store/workspace";

/**
 * Переход в любой диалог любого аккаунта по ⌘K / Ctrl+K.
 *
 * Без него, чтобы открыть переписку, нужно помнить, на каком она аккаунте,
 * переключиться туда в сайдбаре и уже там искать — на десятке аккаунтов это
 * основная потеря времени. Поиск идёт на сервере: списки чатов неоткрытых
 * аккаунтов в браузере не загружены.
 */
export function QuickSwitcher() {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [cursor, setCursor] = useState(0);
  const navigate = useNavigate();
  const selectChatWithAccount = useWorkspace((s) => s.selectChatWithAccount);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen((v) => !v);
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // Ввод обгоняет сеть — запрашиваем не чаще, чем раз в 200 мс простоя.
  const [debounced, setDebounced] = useState("");
  useEffect(() => {
    const t = window.setTimeout(() => setDebounced(q), 200);
    return () => window.clearTimeout(t);
  }, [q]);

  const { data: hits = [], isFetching } = useQuery({
    queryKey: ["chat-search", debounced],
    queryFn: () => api.searchChats(debounced),
    enabled: open,
    staleTime: 10_000,
  });

  useEffect(() => {
    setCursor(0);
  }, [hits]);

  useEffect(() => {
    if (open) {
      setQ("");
      // Автофокус радиксовского диалога срабатывает позже монтирования.
      window.setTimeout(() => inputRef.current?.focus(), 30);
    }
  }, [open]);

  const items = useMemo(() => hits.slice(0, 20), [hits]);

  function go(index: number) {
    const hit = items[index];
    if (!hit) return;
    selectChatWithAccount(hit.account_id, hit.chat_id);
    navigate("/");
    setOpen(false);
  }

  function onKeyDown(e: React.KeyboardEvent) {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setCursor((c) => Math.min(c + 1, items.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setCursor((c) => Math.max(c - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      go(cursor);
    }
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="top-[15%] translate-y-0 gap-0 p-0 sm:max-w-xl [&>button]:hidden">
        {/* Radix требует заголовок для скринридеров; визуально он тут лишний. */}
        <DialogTitle className="sr-only">Быстрый переход к диалогу</DialogTitle>
        <div className="relative border-b border-border">
          <Search className="pointer-events-none absolute left-3.5 top-3.5 h-4 w-4 text-muted-foreground" />
          <Input
            ref={inputRef}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="Диалог по имени или @тегу — по всем аккаунтам"
            className="h-11 border-0 pl-10 text-sm focus-visible:ring-0"
          />
        </div>

        <div className="max-h-96 overflow-y-auto">
          {items.length === 0 ? (
            <div className="flex flex-col items-center gap-2 p-8 text-center text-sm text-muted-foreground">
              <MessagesSquare className="h-8 w-8 opacity-20" />
              {isFetching ? "Ищу…" : "Ничего не нашлось"}
            </div>
          ) : (
            <ul>
              {items.map((h, i) => {
                const title = h.title || (h.username ? `@${h.username}` : `id ${h.chat_id.slice(0, 8)}`);
                return (
                  <li key={`${h.account_id}:${h.chat_id}`}>
                    <button
                      onMouseEnter={() => setCursor(i)}
                      onClick={() => go(i)}
                      className={cn(
                        "flex w-full items-center gap-3 px-4 py-2.5 text-left transition-colors",
                        i === cursor && "bg-accent"
                      )}
                    >
                      <span className="shrink-0 text-muted-foreground">
                        {h.kind === "group" ? (
                          <Users2 className="h-4 w-4" />
                        ) : h.kind === "channel" ? (
                          <Hash className="h-4 w-4" />
                        ) : (
                          <MessagesSquare className="h-4 w-4" />
                        )}
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="flex items-center gap-2">
                          <span className="truncate text-sm font-medium">{title}</span>
                          {h.unread_count > 0 && (
                            <span className="flex h-5 min-w-[20px] shrink-0 items-center justify-center rounded-full bg-primary px-1.5 text-[11px] font-semibold text-primary-foreground">
                              {h.unread_count}
                            </span>
                          )}
                        </span>
                        <span
                          className="block truncate text-[11px]"
                          style={{ color: colorFromString(h.account_id) }}
                        >
                          {h.account_label}
                        </span>
                      </span>
                      <span className="shrink-0 text-[11px] text-muted-foreground">
                        {fmtTime(h.last_message_at)}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <div className="flex items-center gap-3 border-t border-border px-4 py-2 text-[11px] text-muted-foreground">
          <span>↑↓ — выбор</span>
          <span>Enter — открыть</span>
          <span>Esc — закрыть</span>
        </div>
      </DialogContent>
    </Dialog>
  );
}
